import React, { useState } from 'react';
import { Card, Row, Col, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faBullseye, faArrowTrendUp } from '@fortawesome/free-solid-svg-icons';
import avatarImg from 'assets/img/team/40x40/avatar.webp';

interface SalesPersonData {
    id: string;
    name: string;
    position: string;
    target: number;
    achieved: number;
    avatar?: string;
    rank: number;
}

interface SalesPersonCardProps {
    salesPerson: SalesPersonData;
}

const SalesPersonCard: React.FC<SalesPersonCardProps> = ({ salesPerson }) => {
    const [imageError, setImageError] = useState(false);
    const progressPercentage = Math.round((salesPerson.achieved / salesPerson.target) * 100);
    const isOverAchieved = progressPercentage >= 100;
    const isCloseToTarget = progressPercentage >= 80;

    // Função para formatar valores monetários
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const getProgressVariant = () => {
        if (isOverAchieved) return 'success';
        if (isCloseToTarget) return 'warning';
        return 'danger';
    };

    const getRankIcon = () => {
        if (salesPerson.rank <= 3) {
            return (
                <div style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    background: salesPerson.rank === 1 ? '#fbbf24' : salesPerson.rank === 2 ? '#9ca3af' : '#f59e0b',
                    color: 'white',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    border: '3px solid white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}>
                    {salesPerson.rank === 1 ? '👑' : salesPerson.rank === 2 ? '🥈' : '🥉'}
                </div>
            );
        } else {
            return (
                <div style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    background: '#6b7280',
                    color: 'white',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    border: '3px solid white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}>
                    {salesPerson.rank}
                </div>
            );
        }
    };

    const handleImageError = () => {
        setImageError(true);
    };

    return (
        <Card className="h-100 border-0 shadow-sm hover-shadow-lg transition-shadow">
            <Card.Body className="p-4">
                <Row className="align-items-center mb-3">
                    <Col xs="auto">
                        <div className="position-relative" style={{ 
                            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))'
                        }}>
                            <img
                                src={imageError ? avatarImg : (salesPerson.avatar || avatarImg)}
                                alt={salesPerson.name}
                                className="rounded-circle"
                                style={{
                                    width: '60px',
                                    height: '60px',
                                    objectFit: 'cover',
                                    border: `3px solid ${isOverAchieved ? '#10b981' : isCloseToTarget ? '#f59e0b' : '#ef4444'}`,
                                    backgroundColor: 'white',
                                    transition: 'all 0.3s ease'
                                }}
                                onError={handleImageError}
                            />
                            
                            {/* Badge de posição */}
                            {getRankIcon()}
                            
                            {/* Indicador de meta atingida */}
                            {isOverAchieved && (
                                <div style={{
                                    position: 'absolute',
                                    bottom: '-6px',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    background: '#10b981',
                                    color: 'white',
                                    borderRadius: '12px',
                                    padding: '3px 8px',
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                    border: '2px solid white',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}>
                                    🎯
                                </div>
                            )}
                        </div>
                    </Col>
                    <Col>
                        <div className="d-flex justify-content-between align-items-start">
                            <div>
                                <h6 className="mb-1 font-weight-bold">{salesPerson.name}</h6>
                                <p className="text-muted mb-0 font-size-sm">{salesPerson.position}</p>
                            </div>
                        </div>
                    </Col>
                </Row>

                <div className="mb-3">
                    <div className="d-flex justify-content-between mb-2">
                        <span className="font-size-sm text-muted">Progresso</span>
                        <span className={`font-size-sm font-weight-bold ${isOverAchieved ? 'text-success' : isCloseToTarget ? 'text-warning' : 'text-primary'}`}>
                            {progressPercentage}%
                        </span>
                    </div>
                    <ProgressBar
                        now={Math.min(progressPercentage, 100)}
                        variant={getProgressVariant()}
                        style={{ height: '8px' }}
                        className="rounded-pill"
                    />
                    {isOverAchieved && (
                        <div className="mt-1">
                            <small className="text-success">
                                <FontAwesomeIcon icon={faArrowTrendUp} className="me-1" />
                                Meta superada!
                            </small>
                        </div>
                    )}
                </div>

                <Row className="text-center">
                    <Col>
                        <div className="border-end">
                            <h6 className="mb-1 font-weight-bold text-primary">
                                {formatCurrency(salesPerson.achieved)}
                            </h6>
                            <small className="text-muted">Atingido</small>
                        </div>
                    </Col>
                    <Col>
                        <div>
                            <h6 className="mb-1 font-weight-bold text-muted">
                                {formatCurrency(salesPerson.target)}
                            </h6>
                            <small className="text-muted">Meta</small>
                        </div>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

export default SalesPersonCard;
